import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALargeSmall, Check } from 'lucide-react';
import { useUIStore } from '@/lib/store';
import { HintTooltip } from '@/components/ui/HintTooltip';
import { UI_SCALE_LEVELS, applyUiScale } from '@/lib/ui-scale';
import { cn } from '@/lib/cn';

/*
 * ScaleSwitcher (Sessio 21.5): UI-luettavuusskaalaus (portaittainen koko-UI-zoom).
 *
 * Desktop (lg): nakyva segmentti, jossa 4 nappia kasvavalla "A":lla (role=radiogroup) —
 * Petrin kuvaama nakyva portaikko. Mobiili: kompakti dropdown (LanguageSwitcher-patterni,
 * T9-natiivi, ei Radix). Valinta -> setUiScale (persist) + applyUiScale (juuren --ui-scale).
 * Ks. lib/ui-scale.ts + globals.css.
 */

// A-kirjaimen kasvava koko per porras (em = suhteessa Headerin fonttiin).
const A_SIZE = ['text-[0.7em]', 'text-[0.85em]', 'text-[1em]', 'text-[1.2em]'];

export function ScaleSwitcher() {
  const { t } = useTranslation();
  const uiScale = useUIStore((s) => s.uiScale);
  const setUiScale = useUIStore((s) => s.setUiScale);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = (scale: number) => {
    setOpen(false);
    setUiScale(scale);
    applyUiScale(scale);
  };

  const label = (scale: number) => t('header.scale.level', { percent: Math.round(scale * 100) });

  return (
    <>
      {/* Desktop: nakyva segmentti (painikkeet 1·2·3·4) */}
      <div
        role="radiogroup"
        aria-label={t('header.scale.ariaLabel')}
        className="hidden items-center gap-0.5 rounded-sm border border-kaste bg-usva p-0.5 lg:flex"
        data-testid="scale-switcher-segment"
      >
        {UI_SCALE_LEVELS.map((scale, i) => {
          const active = scale === uiScale;
          return (
            <button
              key={scale}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={label(scale)}
              title={label(scale)}
              onClick={() => select(scale)}
              data-testid={`scale-switcher-level-${scale}`}
              className={cn(
                'flex h-6 min-w-6 items-center justify-center rounded-xs px-1 font-display leading-none focus-ring',
                A_SIZE[i],
                active ? 'bg-sammal text-white' : 'text-usva-t hover:bg-valo hover:text-metsa',
              )}
            >
              A
            </button>
          );
        })}
      </div>

      {/* Mobiili: kompakti dropdown */}
      <div ref={containerRef} className="relative lg:hidden" data-testid="scale-switcher">
        <HintTooltip content={t('hints.header.textScale')} side="bottom">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={t('header.scale.ariaLabel')}
            aria-haspopup="menu"
            aria-expanded={open}
            className="flex h-8 w-8 items-center justify-center rounded-sm text-metsa hover:bg-usva focus-ring"
          >
            <ALargeSmall className="h-4 w-4" aria-hidden="true" />
          </button>
        </HintTooltip>
        {open && (
          <div
            role="menu"
            aria-label={t('header.scale.menuLabel')}
            className="absolute right-0 top-9 z-30 w-40 overflow-hidden rounded-sm border border-kaste bg-white shadow-lg"
          >
            {UI_SCALE_LEVELS.map((scale) => {
              const active = scale === uiScale;
              return (
                <button
                  key={scale}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => select(scale)}
                  data-testid={`scale-switcher-option-${scale}`}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-teksti hover:bg-usva focus-ring"
                >
                  <span>{label(scale)}</span>
                  {active && <Check className="h-4 w-4 text-sammal" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
